import { Box } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { IUnitNode } from "../../../../models/unit/unitNode";
import { IUnitNodePermissions } from "../../../../models/unit/unitNodePermissions";
import { IUnitPermissionUserGroup } from "../../../../models/userGroup/unitPermissionUserGroup";
import { ConfirmationButton } from "../../../framework/Button/ConfirmationButton";
import { DialogSettingsCard } from "../../../framework/Container/DialogSettingsCard";
import { UnitApplicationPermissions } from "../../../framework/Unit/UnitApplicationPermissions";
import { UnitPermissionSummaryDialog } from "../UnitPermissionSummaryDialog";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

export interface IUnitPermissionSettingsProp {
    classes?: string;
    infoText?: string;
    companyId: string;
    hideAllUsers?: boolean;
    hideUserGroups?: boolean;
    unitNodePermissions: IUnitNodePermissions;
    accessRightNoneTitle?: string;
    accessRightReadTitle?: string;
    accessRightWriteTitle?: string;
    accessRightWriteHidden?: boolean;
    additionalPermissionATitle?: string;
    additionalPermissionBTitle?: string;
    additionalPermissionCTitle?: string;
    validAdditionalPermissions?: number[];
    partnerCompaniesTitle?: string;
    cardView?: boolean;
    onSave: (obj: any) => Promise<ApiSuccess>;
    onOk: (closeDialog: boolean) => void;
    onCancel: (closeDialog: boolean) => void;
}

export interface IUnitPermissionSettingsState {
    accessRightNoneTitle: string;
    accessRightReadTitle: string;
    accessRightWriteTitle: string;
    additionalPermissionATitle: string;
    additionalPermissionBTitle: string;
    additionalPermissionCTitle: string;
    validAdditionalPermissions: number[];
    userGroups: IUnitPermissionUserGroup[];
    unitNode: IUnitNode;
    saveUnitNode: IUnitNode;
    showPermissionsSummary: boolean;
}

export class UnitPermissionSettings extends React.Component<IUnitPermissionSettingsProp, IUnitPermissionSettingsState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IUnitPermissionSettingsProp) => {
        const unitNodePermissions = props.unitNodePermissions;
        const userGroups = unitNodePermissions?.userGroups?.slice(0) ?? [];
        return {
            accessRightNoneTitle: props.accessRightNoneTitle ? props.accessRightNoneTitle : Translations.AccessRightNone,
            accessRightReadTitle: props.accessRightReadTitle ? props.accessRightReadTitle : "",
            accessRightWriteTitle: props.accessRightWriteHidden ? "" : (props.accessRightWriteTitle ? props.accessRightWriteTitle : Translations.AccessRightWrite),
            additionalPermissionATitle: props.additionalPermissionATitle ? props.additionalPermissionATitle : "",
            additionalPermissionBTitle: props.additionalPermissionBTitle ? props.additionalPermissionBTitle : "",
            additionalPermissionCTitle: props.additionalPermissionCTitle ? props.additionalPermissionCTitle : "",
            validAdditionalPermissions: props.validAdditionalPermissions ? props.validAdditionalPermissions : [],
            userGroups,
            unitNode: unitNodePermissions?.unitNode,
            saveUnitNode: unitNodePermissions?.unitNode,
            showPermissionsSummary: false
        };
    };

    constructor(props: IUnitPermissionSettingsProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    getSaveDataFromState = (props: IUnitPermissionSettingsProp, state: IUnitPermissionSettingsState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("permissions", state.saveUnitNode?.getJsonUnitNodeSaves([], null, null) ?? []);
        return result;
    };

    componentDidUpdate(prevProps: IUnitPermissionSettingsProp, prevState: IUnitPermissionSettingsState) {
        const props = this.props;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevUnitNodeId = prevProps.unitNodePermissions?.unitNode?.id ?? "";
        const unitNodeId = props.unitNodePermissions?.unitNode?.id ?? "";
        if (companyId === prevCompanyId && prevUnitNodeId === unitNodeId) return;
        const newState = this.getStateFromProps(props);
        this.setState(newState);
        this.orgStateHash = this.getSaveDataFromState(props, newState).hash;
    }

    handleChangeSaveUnitNode = (saveUnitNode: IUnitNode) => {
        this.setState({
            saveUnitNode
        });
    };

    saveToDb = async (closeDialog: boolean) => {
        const props = this.props;
        const state = this.state;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => props.onSave(saveData.obj));
        if (!result) return;
        // Save new hash since the window stays open in some scenarios
        this.orgStateHash = saveData.hash;
        props.onOk(closeDialog);
    };

    handleSave = () => {
        this.saveToDb(true);
    };

    handleCancel = async (closeDialog: boolean) => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return false;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            await this.saveToDb(closeDialog);
        } else {
            props.onCancel(closeDialog);
        }

        return true;
    };

    handleShowPermissions = () => {
        this.setState({
            showPermissionsSummary: true
        });
    };

    handleHidePermissions = () => {
        this.setState({
            showPermissionsSummary: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.unitNode) return null;
        return (
            <Box display="flex" flexDirection="column" flexGrow={2}>
                <Box display="flex" flexDirection="column" flexGrow={2} sx={{ marginBottom: "1.9rem" }}>
                    <DialogSettingsCard
                        fitToContainerHeight
                        title={props.cardView ? Translations.Permissions : null}
                        info={props.infoText}
                        showCard={props.cardView}
                        content={
                            <UnitApplicationPermissions
                                companyId={props.companyId}
                                hideAllUsers={props.hideAllUsers}
                                hideUserGroups={props.hideUserGroups}
                                unitId={state.unitNode?.id}
                                groups={state.userGroups}
                                unitNode={state.unitNode}
                                accessRightNoneTitle={state.accessRightNoneTitle}
                                accessRightReadTitle={state.accessRightReadTitle}
                                accessRightWriteTitle={state.accessRightWriteTitle}
                                additionalPermissionTitle={[
                                    state.additionalPermissionATitle,
                                    state.additionalPermissionBTitle,
                                    state.additionalPermissionCTitle
                                ]}
                                cardView={props.cardView}
                                validAdditionalPermissions={state.validAdditionalPermissions}
                                partnerCompaniesTitle={props.partnerCompaniesTitle}
                                partnerCompanies={props.unitNodePermissions.partnerCompanies}
                                onChangeSaveUnitNode={this.handleChangeSaveUnitNode}
                            />
                        } />
                    {state.showPermissionsSummary &&
                        <UnitPermissionSummaryDialog
                            groups={state.userGroups}
                            hideAllUsers={props.hideAllUsers}
                            hideUserGroups={props.hideUserGroups}
                            unitNode={state.saveUnitNode}
                            accessRightNoneTitle={state.accessRightNoneTitle}
                            accessRightReadTitle={state.accessRightReadTitle}
                            accessRightWriteTitle={state.accessRightWriteTitle}
                            additionalPermissionTitle={[
                                state.additionalPermissionATitle,
                                state.additionalPermissionBTitle,
                                state.additionalPermissionCTitle
                            ]}
                            validAdditionalPermissions={state.validAdditionalPermissions}
                            onCancel={this.handleHidePermissions}
                        />
                    }
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <AppButton variant="contained" onClick={this.handleShowPermissions}>{Translations.SetPermissions}</AppButton>
                    <Box display="flex" alignItems="center">
                        <AppButton variant="outlined" onClick={() => this.handleCancel(true)} sx={{ marginRight: ".5rem" }}>{Translations.Close}</AppButton>
                        <ConfirmationButton onClick={this.handleSave} disabled={this.getSaveDataFromState(props, state).hash === this.orgStateHash}>{Translations.Save}</ConfirmationButton>
                    </Box>
                </Box>
            </Box>
        );
    }
}